


















import Vue from 'vue';
import Component from 'vue-class-component';
import { getModule } from 'vuex-module-decorators';
import { loadScript } from 'vue-plugin-load-script';
import {
  MOVIDESK_CLIENT_VARIABLE,
  MOVIDESK_CHAT_URL,
} from '@config/movidesk.ts';
import { HUBSPOT_TRACKING_CODE } from '@config/hubspot.ts';
import AuthenticationModule from '@/stores/modules/AuthenticationModule';
import HubSpotRepository from '@/repositories/hub-spot/HubSpotRepository';
import Module from '@/domain/models/Module';
import Drawer from './drawer/Drawer.vue';
import Toolbar from './toolbar/Toolbar.vue';

interface Window {
  movideskChatWidgetChangeWindowState: any;
  onload: any;
  movideskLogin: any;
  postMessage: any;
}

declare var window: Window;

@Component({
  components: {
    Drawer,
    Toolbar,
  },
})
export default class AppView extends Vue {
  companyVisible: boolean = false;
  authStore?: AuthenticationModule;

  hubSpotRepository: HubSpotRepository = new HubSpotRepository();

  created() {
    this.authStore = getModule(AuthenticationModule, this.$store);
    this.importMovideskCdi();
  }

  mounted() {
    this.initHubSpotWhenReady();
  }

  private async initHubSpotWhenReady(): Promise<void> {
    const checkUser = () => this.$store.state.authentication?.user?.email;

    if (checkUser()) {
      await this.importHubSpot();
    } else {
      const unwatch = this.$watch(
        () => this.$store.state.authentication?.user?.email,
        async (newVal) => {
          if (newVal) {
            unwatch();
            await this.importHubSpot();
          }
        },
        { immediate: true },
      );
    }
  }

  get currentModule(): Module | null {
    return (this.authStore && this.authStore!.currentModule) ?? null;
  }

  get hideAll(): boolean {
    return (
      this.$route.path === '/login'
      || this.$route.path === '/register'
      || this.$route.path == '/logout'
      || this.$route.path == '/reset-password'
    );
  }

  get hideDrawer(): boolean {
    return this.hideAll || this.currentModule == null;
  }

  actionCompany(value: boolean) {
    this.companyVisible = value;
  }

  public importMovideskCdi(): void {
    if (
      !MOVIDESK_CLIENT_VARIABLE
      || !MOVIDESK_CHAT_URL
      || !Number(this.$session.get('user_access-chat_movidesk'))
    ) {
      return;
    }

    if (!this.authStore) {
      return;
    }

    if (
      !this.authStore.user.movidesk_id
      || this.authStore.user.movidesk_id === 'null'
    ) {
      return;
    }

    const movideskCode = document.createElement('script');

    movideskCode.setAttribute('type', 'text/javascript');

    movideskCode.innerHTML = `var mdChatClient="${MOVIDESK_CLIENT_VARIABLE}";`;
    movideskCode.id = 'mdChatClient';

    document.head.appendChild(movideskCode);

    loadScript(MOVIDESK_CHAT_URL).then(() => {
      this.startMovideskChatAuthenticated();
    });
  }

  public async importHubSpot(): Promise<void> {
    if (
      !HUBSPOT_TRACKING_CODE
      || !Number(this.$session.get('user_access-chat_hubspot'))
    ) {
      return;
    }

    const iframeHubspotEl = document.getElementById('hubspot-conversations-iframe');

    if (iframeHubspotEl) {
      iframeHubspotEl.style.removeProperty('display');
    }

    const w = window as any;

    // 1. Obter o token PRIMEIRO
    const token = await this.getInnCashHubSpotAuthToken();
    if (!token) return;

    const userEmail = this.$store.state.authentication.user.email;

    // 2. Limpar identificação anterior (importante para troca de usuário)
    if (w.HubSpotConversations) {
      try {
        w.HubSpotConversations.clear({ resetWidget: true });
      } catch (e) {
        console.log('HubSpot clear error:', e);
      }
    }

    // 3. Configurar settings ANTES de carregar/recarregar o widget
    w.hsConversationsSettings = {
      identificationEmail: userEmail,
      identificationToken: token,
      loadImmediately: false,
    };

    // 4. Se o script já existe, apenas recarregar com nova identificação
    if (document.getElementById('hs-script-loader')) {
      if (w.HubSpotConversations?.widget) {
        w.HubSpotConversations.widget.load();
      }
      return;
    }

    // 5. Registrar callback para primeira carga
    w.hsConversationsOnReady = [
      () => {
        console.log('HubSpot ready for user:', userEmail);
        w.HubSpotConversations.widget.load();
      },
    ];

    // 6. Adicionar o script
    const script = document.createElement('script');
    script.src = `//js.hs-scripts.com/${HUBSPOT_TRACKING_CODE}.js`;
    script.async = true;
    script.defer = true;
    script.id = 'hs-script-loader';

    document.head.appendChild(script);
  }

  public async getInnCashHubSpotAuthToken(): Promise<string | undefined> {
    try {
      const companyGroupId = this.$store.state.authentication.user.company_group_id;

      const token = await this.hubSpotRepository.getInnCashUserHubSpotToken(
        companyGroupId,
      );

      return token;
    } catch (error) {
      this.$notification.error(
        'Houve um problema ao fazer a autenticação com o HubSpot',
      );

      return undefined;
    }
  }

  public startMovideskChatAuthenticated(): void {
    // eslint-disable-next-line no-undef
    window.postMessage(
      {
        action: 'chatWidgetChangeWindowState',
        data: 'minimized',
      },
      '*',
    );

    window.movideskLogin({
      name: this.authStore!.user.name,
      email: this.authStore!.user.email,
      codeReference: this.authStore!.user.movidesk_id,
      stayConnected: false,
      emptySubject: false,
      startChat: false,
    });

    const classElement = document.getElementById('md-app-widget');

    if (classElement !== null) {
      classElement.style.visibility = 'visible';
    }
  }
}
